import { isEmpty } from 'lodash'
import {
  Store,
  StoreBizHour,
  StoreHoliday,
  defaultTZ,
  displayHourFormat,
} from 'models'
import moment from 'moment-timezone'

export const cloneToTzTime = (
  moment: moment.Moment,
  tz?: string,
  keepLocalTime?: boolean,
): moment.Moment => {
  return moment.clone().tz(tz ?? defaultTZ, keepLocalTime)
}

export const currentTzTime = (tz?: string): moment.Moment => {
  return cloneToTzTime(moment(), tz)
}

export const toTzTime = (isoTimeStr?: string, tz?: string): moment.Moment => {
  return cloneToTzTime(moment(isoTimeStr), tz)
}

export const parseTzTime = (
  timeStr?: string,
  format?: string,
  tz?: string,
  keepLocalTime?: boolean,
): moment.Moment => {
  if (isEmpty(timeStr)) {
    return currentTzTime(tz)
  }
  return cloneToTzTime(moment(timeStr, format), tz, keepLocalTime)
}

export const formatTime = (
  isoTimeStr?: string,
  tz?: string,
  timeFormat?: string,
): string => {
  return toTzTime(isoTimeStr, tz).format(timeFormat ?? displayHourFormat)
}

export const isToday = (date: moment.Moment, tz?: string): boolean => {
  return cloneToTzTime(date, tz)
    .startOf('day')
    .isSame(currentTzTime(tz).startOf('day'))
}

export const setTime = (mDate: moment.Moment, time?: string): moment.Moment => {
  if (time === undefined || isEmpty(time)) {
    return mDate.clone()
  }

  const regex = new RegExp('^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$')
  if (!regex.test(time)) {
    return mDate.clone()
  }

  let storeTime = mDate.clone()
  const [hours, minutes] = time.split(':').map((t) => parseInt(t))
  storeTime.set({ hours, minutes }).startOf('minute')

  return storeTime
}

export const isInWorkingHoursHoliday = (
  date: Date | string | moment.Moment,
  holidays?: StoreHoliday[],
  timezone?: string,
): [boolean, StoreHoliday?] => {
  // If the validated holidays is empty, then the date is always invalid
  if (holidays === undefined || isEmpty(holidays)) {
    return [false]
  }

  const mDate = cloneToTzTime(moment(date), timezone)
  // holiday.date is a plain calendar date (YYYY-MM-DD) — compare it as a
  // calendar date in the store timezone, never shift it across timezones.
  const targetDate = mDate.format('YYYY-MM-DD')
  const holiday = holidays.find((h) => h.date === targetDate)
  if (holiday === undefined) {
    return [false]
  } else if (!holiday.is_working) {
    return [false, holiday]
  }
  const from1 = setTime(mDate, holiday.from_1)
  const to1 = setTime(mDate, holiday.to_1)
  const from2 = setTime(mDate, holiday.from_2)
  const to2 = setTime(mDate, holiday.to_2)

  return [
    mDate.isBetween(from1, to1, 'minutes', '[)') ||
      mDate.isBetween(from2, to2, 'minutes', '[)'),
    holiday,
  ]
}

export const isInBizHour = (
  date: Date | string | moment.Moment,
  bizHours?: StoreBizHour[],
  timezone?: string,
): [boolean, StoreBizHour?] => {
  // If the validated biz hours is empty, then the date is always valid
  if (bizHours === undefined || isEmpty(bizHours)) {
    return [true]
  }

  let mDate = cloneToTzTime(moment(date), timezone)
  const bizHour = bizHours.find((h) => h?.day_of_week === mDate.weekday())
  if (!bizHour || !bizHour.is_working) {
    return [false]
  }

  const from1 = setTime(mDate, bizHour.from_1)
  const to1 = setTime(mDate, bizHour.to_1)
  const from2 = setTime(mDate, bizHour.from_2)
  const to2 = setTime(mDate, bizHour.to_2)

  return [
    mDate.isBetween(from1, to1, 'minutes', '[)') ||
      mDate.isBetween(from2, to2, 'minutes', '[)'),
    bizHour,
  ]
}

export const isStoreOpening = (
  store: Store,
  currentDate: Date | string | moment.Moment,
): [boolean, StoreBizHour?] => {
  const [isWorkingHoliday, holiday] = isInWorkingHoursHoliday(
    currentDate,
    store.holidays,
    store.timezone,
  )

  if (holiday !== undefined) {
    return [isWorkingHoliday, { ...holiday }]
  } else {
    return isInBizHour(currentDate, store.biz_hours, store.timezone)
  }
}

export const getNextOpeningTime = (
  now: moment.Moment,
  isOpening: boolean,
  bizHour: StoreBizHour | undefined,
): moment.Moment | undefined => {
  if (isOpening || bizHour?.is_working !== true) {
    return undefined
  }

  const from1 = setTime(now, bizHour?.from_1)
  if (now.isBefore(from1)) {
    return from1
  }

  const from2 = setTime(now, bizHour?.from_2)
  if (now.isBefore(from2)) {
    return from2
  }
}

export const convert24hrTo12hr = (time: string, tz?: string): string => {
  const currentDate = currentTzTime(tz).startOf('day')
  const storeTime = setTime(currentDate, time)
  return storeTime.format('hh:mm A')
}

export const convertToWeekday = (dayOfWeek: number): string => {
  if (dayOfWeek < 0 || dayOfWeek > 6) {
    return ''
  }
  return moment().day(dayOfWeek).format('dddd')
}
